/**
 * Enforcement Mode Flip Dialog — Phase 1c.2
 *
 * super_admin only. Typed CONFIRM + reason ≥10 chars. Audited.
 */
import { useState } from "react";
import { ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  getNetworkEnforcementMode,
  setNetworkEnforcementMode,
  type NetworkEnforcementMode,
} from "@/data/contractor/networkExecution";
import { toast } from "sonner";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  actorId: string;
  actorRole: string;
  isSuperAdmin: boolean;
  onChanged?: (mode: NetworkEnforcementMode) => void;
}

export function EnforcementModeFlipDialog({
  open,
  onOpenChange,
  actorId,
  actorRole,
  isSuperAdmin,
  onChanged,
}: Props) {
  const current = getNetworkEnforcementMode();
  const target: NetworkEnforcementMode = current === "soft" ? "hard" : "soft";

  const [reason, setReason] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = () => {
    setError(null);
    if (!isSuperAdmin) {
      setError("Only super admins can change enforcement mode.");
      return;
    }
    if (confirmText.trim() !== "CONFIRM") {
      setError("Type CONFIRM exactly to proceed.");
      return;
    }
    if (reason.trim().length < 10) {
      setError("Reason must be at least 10 characters.");
      return;
    }
    const r = setNetworkEnforcementMode({
      toMode: target,
      actorId,
      actorRole,
      reason,
      confirmedSuperAdmin: true,
    });
    if (!r.ok) {
      setError(r.reason ?? "Cannot change enforcement mode.");
      return;
    }
    toast.success(`Enforcement mode set to ${target.toUpperCase()}.`);
    setReason("");
    setConfirmText("");
    onOpenChange(false);
    onChanged?.(r.mode);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-destructive" />
            Change network enforcement mode
          </DialogTitle>
          <DialogDescription>
            Currently <strong className="uppercase">{current}</strong>. This action will switch to{" "}
            <strong className="uppercase">{target}</strong> and emit a 24h system banner.
            {target === "hard"
              ? " In hard mode, recommended side-effects may auto-execute."
              : " Soft mode keeps everything advisory and manual."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium">Reason (≥ 10 characters)</label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this change required right now?"
              rows={3}
            />
          </div>
          <div>
            <label className="text-xs font-medium">Type CONFIRM to proceed</label>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="CONFIRM"
            />
          </div>
          {error && <div className="text-xs text-destructive">{error}</div>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleSubmit}>
            Switch to {target.toUpperCase()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
